import React, { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

export default function AttachmentsPanel({ caseId }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/cases/${caseId}/attachments`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [caseId]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/cases/${caseId}/attachments`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Screenshot uploaded");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/cases/${caseId}/attachments/${id}`);
      toast.success("Removed");
      await load();
    } catch {
      toast.error("Could not remove");
    }
  };

  const token = localStorage.getItem("phr_token") || "";

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-slate-500" />
          <p className="text-sm font-semibold text-slate-900">Screenshots ({items.length})</p>
        </div>
        <Button
          onClick={() => fileRef.current?.click()}
          size="sm"
          className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-9"
          disabled={busy}
          data-testid="attach-upload-button"
        >
          <Upload className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Uploading…" : "Attach"}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          data-testid="attach-file-input"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
        />
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-slate-500">Attach your cancellation confirmation and charge screenshots so you can paste them alongside each message.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="attachments-grid">
          {items.map((a) => {
            const url = `${BACKEND_URL}/api/files/${a.storage_path}?auth=${encodeURIComponent(token)}`;
            return (
              <div key={a.id} className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-50" data-testid={`attachment-${a.id}`}>
                <a href={url} target="_blank" rel="noreferrer">
                  <img src={url} alt={a.original_filename} className="w-full h-full object-cover" />
                </a>
                <button
                  onClick={() => remove(a.id)}
                  className="absolute top-1.5 right-1.5 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 bg-white/95 hover:bg-red-50 text-red-600 rounded-full p-1.5 shadow border border-slate-200 transition-opacity"
                  data-testid={`attachment-delete-${a.id}`}
                  aria-label="Remove attachment"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
